import { configureStore } from '@reduxjs/toolkit';
import { createEpicMiddleware } from 'redux-observable';
import { combineEpics } from 'redux-observable';
import { createLogger } from 'redux-logger';
import dashboardReducer from './slices/dashboardSlice';
import filtersReducer from './slices/filtersSlice';
import regionMapReducer from './slices/regionMapSlice';
import propensityReducer from './slices/propensitySlice';
import propensityHierarchyReducer from './slices/propensityHierarchySlice';
import renewalNoticeUploadReducer from './slices/renewalNoticeUploadSlice';
import smsTriggerReducer from './slices/smsTriggerSlice';
import smsSchedulerReducer from './slices/smsSchedulerSlice';
import smsTemplateReducer from './slices/smsTemplateSlice';
import renewalDashboardReducer from './slices/renewalDashboardSlice';
import dashboardEpics from './epics/dashboardEpics';
import { regionMapEpics } from './epics/regionMapEpics';
import propensityEpics from './epics/propensityEpics';
import propensityHierarchyEpics from './epics/propensityHierarchyEpics';
import renewalNoticeUploadEpics from './epics/renewalNoticeUploadEpics';
import smsTriggerEpics from './epics/smsTriggerEpics';
import smsSchedulerEpics from './epics/smsSchedulerEpics';
import * as smsTemplateEpics from './epics/smsTemplateEpics';
import renewalDashboardEpics from './epics/renewalDashboardEpics';

// Create middleware
const epicMiddleware = createEpicMiddleware<any, any, any>();
const rootEpic = combineEpics<any, any, any>(
  ...dashboardEpics,
  ...regionMapEpics,
  ...propensityEpics,
  ...propensityHierarchyEpics,
  ...renewalNoticeUploadEpics,
  ...smsTriggerEpics,
  ...smsSchedulerEpics,
  smsTemplateEpics.fetchTemplatesEpic,
  smsTemplateEpics.fetchTemplateEpic,
  smsTemplateEpics.createTemplateEpic,
  smsTemplateEpics.updateTemplateEpic,
  smsTemplateEpics.deleteTemplateEpic,
  ...renewalDashboardEpics
);

// Create logger with options
const logger = createLogger({
  collapsed: true, // Collapse by default for cleaner console
  diff: true, // Show the diff between states
  duration: true, // Print the duration of each action
  timestamp: true // Show timestamps
});

// Check if we're in development mode
const isDevelopment = process.env.NODE_ENV === 'development' || !process.env.NODE_ENV;

export const store = configureStore({
  reducer: {
    dashboard: dashboardReducer,
    filters: filtersReducer,
    regionMap: regionMapReducer,
    propensity: propensityReducer,
    propensityHierarchy: propensityHierarchyReducer,
    renewalNoticeUpload: renewalNoticeUploadReducer,
    smsTrigger: smsTriggerReducer,
    smsScheduler: smsSchedulerReducer,
    smsTemplate: smsTemplateReducer,
    renewalDashboard: renewalDashboardReducer
  },
  middleware: (getDefaultMiddleware) => {
    // Add logger only in development mode
    if (isDevelopment) {
      return getDefaultMiddleware().concat(epicMiddleware, logger);
    }
    return getDefaultMiddleware().concat(epicMiddleware, logger);
  }
});

epicMiddleware.run(rootEpic);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch; 